import React from "react";
import { Link } from "react-router-dom";
import { SectionHeader } from "@/components/shared/Common";
import { Button } from "@/components/ui/button";
import { CheckCircle2, ChevronRight } from "lucide-react";
import FeeComparisonWidget from "@/components/shared/FeeComparisonWidget";
import ReadMore from "@/components/shared/ReadMore";

const points = [
  ["Search vetted professionals by location, qualification, experience, availability, rating and role type.", "Filter across 10+ criteria — see anonymous profiles, not CVs."],
  ["Book individual guards, CPOs/VPOs, or full cluster teams.", "One-click booking flow works the same for solo pros and ready-made teams."],
  ["Reduce time spent sourcing staff and access verified profiles instantly.", "PSA/SIA-verified, references checked, endorsements from past clients."],
  ["Unlock contact details only when ready to proceed — after agreement/payment.", "Pro identity stays anonymous until the contract is signed — GDPR-first."],
  ["View SOPs and agreements before final confirmation.", "Company uploads site brief, dress code, call schedule; pros confirm read-and-agree."],
  ["Endorse staff after completed work and build trusted teams over time.", "Star rating + written endorsement feeds pro reliability score and future bookings."],
];

export default function ForCompanies() {
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
      <SectionHeader
        eyebrow="For Employers"
        title="Source the right team. Move faster. Pay less to place them."
      />
      <ReadMore
        className="max-w-3xl"
        preview="Search vetted professionals by location, qualification, experience, availability, rating and role."
        more="Book individuals or full teams — with clear pricing, verified licences and endorsements. Our transparent introduction fee is a fraction of a traditional agency margin."
        buttonMoreLabel="Read more"
        testId="for-co-intro-readmore"
      />

      {/* Fee comparison widget */}
      <div className="mt-10">
        <FeeComparisonWidget />
      </div>

      {/* Value points */}
      <div className="mt-12">
        <div className="text-xs uppercase tracking-widest text-brand font-outfit font-bold mb-4">Why companies choose SecuSource</div>
        <div className="grid md:grid-cols-2 gap-3">
          {points.map(([headline, detail]) => (
            <details key={headline} className="card-flat p-4 group cursor-pointer">
              <summary className="list-none flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-gold-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1 text-sm text-white font-manrope flex items-start justify-between gap-2">
                  <span>{headline}</span>
                  <ChevronRight className="w-4 h-4 group-open:rotate-90 transition-transform text-brand flex-shrink-0 mt-0.5" />
                </div>
              </summary>
              <div className="mt-2 pl-8 text-sm text-white/70 font-manrope leading-relaxed">{detail}</div>
            </details>
          ))}
        </div>
      </div>

      <div className="mt-10 flex flex-wrap gap-3">
        <Button asChild size="lg" className="bg-brand hover:bg-brand-hover text-white px-6 py-6" data-testid="for-co-register-btn">
          <Link to="/register/company">Register as a Company</Link>
        </Button>
        <Button asChild size="lg" variant="outline" className="border-brand text-brand hover:bg-brand-light px-6 py-6">
          <Link to="/pricing">See detailed pricing</Link>
        </Button>
      </div>
    </div>
  );
}
